import React, { useState } from 'react';
import { OfficerLayout } from '../../components/layout/OfficerLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Modal } from '../../components/ui/Modal';
import { Calendar, MapPin, Users, Recycle, Plus, ChevronRight, CheckCircle, Clock, Camera, Upload, AlertCircle, Navigation, Building, Phone, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
interface EventItem {
  id: string;
  name: string;
  date: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  type: 'cleanup' | 'campaign' | 'workshop';
  hasRegisteredLocation: boolean;
  hasSubmittedOutput: boolean;
  targetParticipants: number;
  targetTonnage?: number;
}
interface LocationRegistration {
  id: string;
  eventId: string;
  venueName: string;
  address: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
}
const mockEvents: EventItem[] = [{
  id: '1',
  name: 'HPSN 2025: Atasi Sampah Plastik',
  date: '21 Feb 2025',
  status: 'ongoing',
  type: 'cleanup',
  hasRegisteredLocation: true,
  hasSubmittedOutput: false,
  targetParticipants: 500,
  targetTonnage: 2
}, {
  id: '2',
  name: 'Mega Clean-Up Ciliwung',
  date: '15 Mar 2025',
  status: 'upcoming',
  type: 'cleanup',
  hasRegisteredLocation: false,
  hasSubmittedOutput: false,
  targetParticipants: 200,
  targetTonnage: 1
}, {
  id: '3',
  name: 'Workshop Ekonomi Sirkular',
  date: '20 Mar 2025',
  status: 'upcoming',
  type: 'workshop',
  hasRegisteredLocation: false,
  hasSubmittedOutput: false,
  targetParticipants: 100
}, {
  id: '4',
  name: 'World Cleanup Day 2024',
  date: '16 Sep 2024',
  status: 'completed',
  type: 'cleanup',
  hasRegisteredLocation: true,
  hasSubmittedOutput: true,
  targetParticipants: 1000,
  targetTonnage: 5
}];
const mockLocations: LocationRegistration[] = [{
  id: '1',
  eventId: '1',
  venueName: 'Pantai Ancol Timur',
  address: 'Jl. Lodan Timur No.7, Ancol, Jakarta Utara',
  status: 'approved',
  submittedAt: '10 Feb 2025'
}];
export function EventOfficerPage() {
  const [activeTab, setActiveTab] = useState<'events' | 'locations' | 'outputs'>('events');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isOutputModalOpen, setIsOutputModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successType, setSuccessType] = useState<'location' | 'output'>('location');
  // Form states
  const [locationForm, setLocationForm] = useState({
    venueName: '',
    address: '',
    coordinates: '-6.1234, 106.8234',
    date: '',
    time: '',
    picName: '',
    picPhone: ''
  });
  const [outputForm, setOutputForm] = useState({
    participants: '',
    tonnage: '',
    notes: ''
  });
  const handleRegisterLocation = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsRegisterModalOpen(false);
      setSuccessType('location');
      setIsSuccess(true);
    }, 1500);
  };
  const handleSubmitOutput = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsOutputModalOpen(false);
      setSuccessType('output');
      setIsSuccess(true);
    }, 1500);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ongoing':
        return <Badge className="bg-green-100 text-green-700 border-green-200 gap-1" data-id="element-2895">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-2896" />
            Berlangsung
          </Badge>;
      case 'upcoming':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-id="element-2897">
            Akan Datang
          </Badge>;
      case 'completed':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200" data-id="element-2898">
            Selesai
          </Badge>;
      default:
        return null;
    }
  };
  const getLocationStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 border-green-200" data-id="element-2899">
            Disetujui
          </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200" data-id="element-2900">
            Menunggu
          </Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-red-200" data-id="element-2901">
            Ditolak
          </Badge>;
      default:
        return null;
    }
  };
  if (isSuccess) {
    return <OfficerLayout title={successType === 'location' ? 'Lokasi Terdaftar' : 'Output Terkirim'} data-id="element-2902">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in zoom-in duration-300" data-id="element-2903">
          <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6" data-id="element-2904">
            <CheckCircle className="h-10 w-10" data-id="element-2905" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2906">
            {successType === 'location' ? 'Lokasi Berhasil Didaftarkan!' : 'Output Event Terkirim!'}
          </h2>
          <p className="text-gray-600 mb-8 max-w-xs" data-id="element-2907">
            {successType === 'location' ? 'Lokasi event Anda sedang menunggu persetujuan dari Admin Pusat.' : 'Data output event telah dikirim dan akan diverifikasi oleh Admin.'}
          </p>
          <div className="flex flex-col gap-3 w-full max-w-xs" data-id="element-2908">
            <Button className="w-full bg-primary-600 hover:bg-primary-700" onClick={() => {
            setIsSuccess(false);
            setSelectedEvent(null);
          }} data-id="element-2909">
              Kembali ke Daftar Event
            </Button>
          </div>
        </div>
      </OfficerLayout>;
  }
  return <OfficerLayout title="Event Nasional" data-id="element-2910">
      {/* Tabs */}
      <div className="flex p-1 bg-gray-100 rounded-lg mb-6" data-id="element-2911">
        {[{
        id: 'events',
        label: 'Event Aktif'
      }, {
        id: 'locations',
        label: 'Lokasi Saya'
      }, {
        id: 'outputs',
        label: 'Output'
      }].map(tab => <button key={tab.id} className={cn('flex-1 py-2 text-xs font-medium rounded-md transition-all', activeTab === tab.id ? 'bg-white shadow text-primary-600' : 'text-gray-500')} onClick={() => setActiveTab(tab.id as any)} data-id="element-2912">
            {tab.label}
          </button>)}
      </div>

      {activeTab === 'events' && <div className="space-y-4 animate-in fade-in duration-300" data-id="element-2913">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3" data-id="element-2914">
            <Card className="bg-green-50 border-green-100" data-id="element-2915">
              <CardContent className="p-3 text-center" data-id="element-2916">
                <p className="text-xl font-bold text-green-700" data-id="element-2917">1</p>
                <p className="text-[10px] text-green-600" data-id="element-2918">Berlangsung</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-100" data-id="element-2919">
              <CardContent className="p-3 text-center" data-id="element-2920">
                <p className="text-xl font-bold text-blue-700" data-id="element-2921">2</p>
                <p className="text-[10px] text-blue-600" data-id="element-2922">Akan Datang</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-100" data-id="element-2923">
              <CardContent className="p-3 text-center" data-id="element-2924">
                <p className="text-xl font-bold text-gray-700" data-id="element-2925">1</p>
                <p className="text-[10px] text-gray-600" data-id="element-2926">Selesai</p>
              </CardContent>
            </Card>
          </div>

          {/* Event List */}
          <div className="space-y-3" data-id="element-2927">
            {mockEvents.map(event => <Card key={event.id} className={cn('overflow-hidden transition-all', event.status === 'ongoing' && 'border-l-4 border-l-green-500')} data-id="element-2928">
                <CardContent className="p-4" data-id="element-2929">
                  <div className="flex justify-between items-start mb-2" data-id="element-2930">
                    {getStatusBadge(event.status)}
                    <span className="text-xs text-gray-500" data-id="element-2931">{event.date}</span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2" data-id="element-2932">{event.name}</h4>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3" data-id="element-2933">
                    <span className="flex items-center gap-1" data-id="element-2934">
                      <Users className="h-3 w-3" data-id="element-2935" />
                      Target: {event.targetParticipants}
                    </span>
                    {event.targetTonnage && <span className="flex items-center gap-1" data-id="element-2936">
                        <Recycle className="h-3 w-3" data-id="element-2937" />
                        Target: {event.targetTonnage} ton
                      </span>}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2" data-id="element-2938">
                    {!event.hasRegisteredLocation && event.status !== 'completed' && <Button size="sm" variant="outline" className="flex-1 text-xs h-9" onClick={() => {
                setSelectedEvent(event);
                setIsRegisterModalOpen(true);
              }} data-id="element-2939">
                          <MapPin className="h-3 w-3 mr-1" data-id="element-2940" />
                          Daftarkan Lokasi
                        </Button>}
                    {event.hasRegisteredLocation && !event.hasSubmittedOutput && <Button size="sm" className="flex-1 text-xs h-9 bg-primary-600" onClick={() => {
                setSelectedEvent(event);
                setIsOutputModalOpen(true);
              }} data-id="element-2941">
                          <Upload className="h-3 w-3 mr-1" data-id="element-2942" />
                          Laporkan Output
                        </Button>}
                    {event.hasRegisteredLocation && <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]" data-id="element-2943">
                        <CheckCircle className="h-3 w-3 mr-1" data-id="element-2944" />
                        Lokasi Terdaftar
                      </Badge>}
                    {event.hasSubmittedOutput && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]" data-id="element-2945">
                        <CheckCircle className="h-3 w-3 mr-1" data-id="element-2946" />
                        Output Terkirim
                      </Badge>}
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>}

      {activeTab === 'locations' && <div className="space-y-4 animate-in fade-in duration-300" data-id="element-2947">
          {mockLocations.length > 0 ? mockLocations.map(loc => <Card key={loc.id} data-id="element-2948">
                <CardContent className="p-4" data-id="element-2949">
                  <div className="flex justify-between items-start mb-2" data-id="element-2950">
                    {getLocationStatusBadge(loc.status)}
                    <span className="text-xs text-gray-500" data-id="element-2951">
                      {loc.submittedAt}
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-2952">
                    {loc.venueName}
                  </h4>
                  <p className="text-xs text-gray-500 flex items-start gap-1" data-id="element-2953">
                    <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" data-id="element-2954" />
                    {loc.address}
                  </p>
                </CardContent>
              </Card>) : <div className="text-center py-12" data-id="element-2955">
              <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" data-id="element-2956" />
              <p className="text-gray-500" data-id="element-2957">Belum ada lokasi terdaftar</p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-2958">
                Daftarkan lokasi event dari tab "Event Aktif"
              </p>
            </div>}
        </div>}

      {activeTab === 'outputs' && <div className="space-y-4 animate-in fade-in duration-300" data-id="element-2959">
          <Card className="bg-blue-50 border-blue-200" data-id="element-2960">
            <CardContent className="p-4" data-id="element-2961">
              <div className="flex items-start gap-3" data-id="element-2962">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2963" />
                <div data-id="element-2964">
                  <p className="text-sm font-medium text-blue-900" data-id="element-2965">
                    Output Event
                  </p>
                  <p className="text-xs text-blue-700 mt-1" data-id="element-2966">
                    Laporkan jumlah peserta dan tonase sampah yang terkumpul
                    setelah event selesai.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Output History */}
          <Card data-id="element-2967">
            <CardContent className="p-4" data-id="element-2968">
              <div className="flex justify-between items-start mb-2" data-id="element-2969">
                <Badge className="bg-green-100 text-green-700 border-green-200" data-id="element-2970">
                  Terverifikasi
                </Badge>
                <span className="text-xs text-gray-500" data-id="element-2971">16 Sep 2024</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-2972">
                World Cleanup Day 2024
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-2973">
                <div data-id="element-2974">
                  <p className="text-xs text-gray-500" data-id="element-2975">Peserta</p>
                  <p className="font-bold text-gray-900" data-id="element-2976">1,250 orang</p>
                </div>
                <div data-id="element-2977">
                  <p className="text-xs text-gray-500" data-id="element-2978">Sampah Terkumpul</p>
                  <p className="font-bold text-gray-900" data-id="element-2979">5.2 ton</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>}

      {/* Register Location Modal */}
      <Modal isOpen={isRegisterModalOpen} onClose={() => setIsRegisterModalOpen(false)} title="Daftarkan Lokasi Event" size="lg" data-id="element-2980">
        {selectedEvent && <div className="space-y-4" data-id="element-2981">
            <div className="p-3 bg-primary-50 rounded-lg border border-primary-100" data-id="element-2982">
              <p className="text-sm font-medium text-primary-900" data-id="element-2983">
                {selectedEvent.name}
              </p>
              <p className="text-xs text-primary-700" data-id="element-2984">{selectedEvent.date}</p>
            </div>

            <div className="space-y-4" data-id="element-2985">
              <div data-id="element-2986">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-2987">
                  Nama Lokasi/Venue <span className="text-red-500" data-id="element-2988">*</span>
                </label>
                <Input placeholder="Contoh: Pantai Ancol Timur" value={locationForm.venueName} onChange={e => setLocationForm({
              ...locationForm,
              venueName: e.target.value
            })} data-id="element-2989" />
              </div>

              <div data-id="element-2990">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-2991">
                  Alamat Lengkap <span className="text-red-500" data-id="element-2992">*</span>
                </label>
                <Textarea placeholder="Jl. Lodan Timur No.7, Ancol, Jakarta Utara" className="h-20" value={locationForm.address} onChange={e => setLocationForm({
              ...locationForm,
              address: e.target.value
            })} data-id="element-2993" />
              </div>

              <div data-id="element-2994">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-2995">
                  Koordinat GPS <span className="text-red-500" data-id="element-2996">*</span>
                </label>
                <div className="flex gap-2" data-id="element-2997">
                  <Input placeholder="Mengambil koordinat..." readOnly className="bg-gray-50 flex-1" value={locationForm.coordinates} data-id="element-2998" />
                  <Button variant="outline" size="icon" data-id="element-2999">
                    <Navigation className="h-4 w-4" data-id="element-3000" />
                  </Button>
                </div>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-3001">
                  <CheckCircle className="h-3 w-3 mr-1" data-id="element-3002" /> GPS Akurat
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-3003">
                <div data-id="element-3004">
                  <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3005">
                    Tanggal Pelaksanaan
                  </label>
                  <Input type="date" value={locationForm.date} onChange={e => setLocationForm({
                ...locationForm,
                date: e.target.value
              })} data-id="element-3006" />
                </div>
                <div data-id="element-3007">
                  <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3008">
                    Jam Mulai
                  </label>
                  <Input type="time" value={locationForm.time} onChange={e => setLocationForm({
                ...locationForm,
                time: e.target.value
              })} data-id="element-3009" />
                </div>
              </div>

              <div data-id="element-3010">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3011">
                  Nama PIC Lokasi <span className="text-red-500" data-id="element-3012">*</span>
                </label>
                <Input placeholder="Nama penanggung jawab di lokasi" value={locationForm.picName} onChange={e => setLocationForm({
              ...locationForm,
              picName: e.target.value
            })} data-id="element-3013" />
              </div>

              <div data-id="element-3014">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3015">
                  No. HP PIC <span className="text-red-500" data-id="element-3016">*</span>
                </label>
                <Input type="tel" placeholder="08xxxxxxxxxx" value={locationForm.picPhone} onChange={e => setLocationForm({
              ...locationForm,
              picPhone: e.target.value
            })} data-id="element-3017" />
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t" data-id="element-3018">
              <Button variant="outline" className="flex-1" onClick={() => setIsRegisterModalOpen(false)} data-id="element-3019">
                Batal
              </Button>
              <Button className="flex-1 bg-primary-600 hover:bg-primary-700" onClick={handleRegisterLocation} disabled={isSubmitting} data-id="element-3020">
                {isSubmitting ? <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-3021" />
                    Mengirim...
                  </> : 'Daftarkan Lokasi'}
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Submit Output Modal */}
      <Modal isOpen={isOutputModalOpen} onClose={() => setIsOutputModalOpen(false)} title="Laporkan Output Event" size="lg" data-id="element-3022">
        {selectedEvent && <div className="space-y-4" data-id="element-3023">
            <div className="p-3 bg-primary-50 rounded-lg border border-primary-100" data-id="element-3024">
              <p className="text-sm font-medium text-primary-900" data-id="element-3025">
                {selectedEvent.name}
              </p>
              <p className="text-xs text-primary-700" data-id="element-3026">{selectedEvent.date}</p>
            </div>

            <div className="space-y-4" data-id="element-3027">
              <div data-id="element-3028">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3029">
                  Jumlah Peserta <span className="text-red-500" data-id="element-3030">*</span>
                </label>
                <Input type="number" placeholder="0" value={outputForm.participants} onChange={e => setOutputForm({
              ...outputForm,
              participants: e.target.value
            })} data-id="element-3031" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-3032">
                  Target: {selectedEvent.targetParticipants} peserta
                </p>
              </div>

              {selectedEvent.type === 'cleanup' && <div data-id="element-3033">
                  <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3034">
                    Tonase Sampah Terkumpul (kg){' '}
                    <span className="text-red-500" data-id="element-3035">*</span>
                  </label>
                  <Input type="number" placeholder="0" value={outputForm.tonnage} onChange={e => setOutputForm({
              ...outputForm,
              tonnage: e.target.value
            })} data-id="element-3036" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-3037">
                    Target: {selectedEvent.targetTonnage} ton (
                    {(selectedEvent.targetTonnage || 0) * 1000} kg)
                  </p>
                </div>}

              <div data-id="element-3038">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3039">
                  Catatan
                </label>
                <Textarea placeholder="Catatan tambahan tentang pelaksanaan event..." className="h-20" value={outputForm.notes} onChange={e => setOutputForm({
              ...outputForm,
              notes: e.target.value
            })} data-id="element-3040" />
              </div>

              <div data-id="element-3041">
                <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-3042">
                  Upload Bukti Foto/Video{' '}
                  <span className="text-red-500" data-id="element-3043">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-3044">
                  <Camera className="h-8 w-8 text-gray-400 mb-2" data-id="element-3045" />
                  <p className="text-sm font-medium text-gray-900" data-id="element-3046">
                    Ambil Foto
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-3047">
                    Min. 3 foto dokumentasi
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t" data-id="element-3048">
              <Button variant="outline" className="flex-1" onClick={() => setIsOutputModalOpen(false)} data-id="element-3049">
                Batal
              </Button>
              <Button className="flex-1 bg-primary-600 hover:bg-primary-700" onClick={handleSubmitOutput} disabled={isSubmitting} data-id="element-3050">
                {isSubmitting ? <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-3051" />
                    Mengirim...
                  </> : 'Kirim Output'}
              </Button>
            </div>
          </div>}
      </Modal>
    </OfficerLayout>;
}